// src/ui/components/dialogs/FullscreenDialog.layouts.tsx
import React, { useEffect } from 'react';
import parse from 'html-react-parser';
import AutoFitText from '../../../core/utils/AutoFitText';
import { DialogContentStyles, DialogHeader, SimpleHeader, SpecsContainer, LoadingSpinner } from './FullscreenDialog.components';
import { LayoutProps, VideoLayoutProps, ImageLayoutProps } from './FullscreenDialog.types';
import { isAutoFitDebugActive, addDebugLog } from '../debug/DebugUtils';

/* 🔒 LIMITADOR FÍSICO REAL PARA TVs GRANDES */
const getSafeMaxFontSize = () => {
  const vh = window.innerHeight || 1080;
  const dpr = window.devicePixelRatio || 1;
  const physicalCap = (vh / dpr) * 0.12;
  return Math.min(Math.floor(physicalCap), 120);
};


/* ========================= TEXT ONLY ========================= */
export const TextOnlyLayout: React.FC<LayoutProps> = ({
  title,
  processedWork,
  parserOptions,
  blockSpecs,
  screenResolution,
}) => {
  const maxFontSize = getSafeMaxFontSize();
  const initialFontSize = Math.min(maxFontSize * 0.65, 80);

  useEffect(() => {
    if (isAutoFitDebugActive()) {
      addDebugLog('TextOnlyLayout', 'Layout renderizado', {
        title,
        textScale: screenResolution.textScale,
        isLargeScreen: screenResolution.isLargeScreen,
        maxFontSize,
        initialFontSize,
        minFontSize: 12,
        lineHeight: 1.4,
        contentLength: processedWork?.length || 0,
      });
    }
  }, [title, screenResolution.textScale, screenResolution.isLargeScreen, maxFontSize, initialFontSize, processedWork]);

  return (
    <div className="w-full h-full relative flex flex-col px-8 py-6">
      <DialogHeader title={title} blockSpecs={blockSpecs} titleStyle="large" />
      <div className="flex-1 flex items-start justify-center pt-16 pb-2 min-h-0 overflow-hidden">

        <AutoFitText
          maxHeight="100%"
          padding={0}
          minFontSize={12}
          maxFontSize={maxFontSize}
          initialFontSize={initialFontSize}
          lineHeight={1.4}
        >
          <DialogContentStyles />
          <div className="prose max-w-none text-white text-center fullscreen-dialog-content">
            {parse(processedWork || '', parserOptions)}
          </div>
        </AutoFitText>
      </div>
    </div>
  );
};

/* ========================= IMAGE ========================= */
export const ImageLayout: React.FC<ImageLayoutProps> = ({
  title,
  processedWork,
  parserOptions,
  blockSpecs,
  screenResolution,
  imageUrl,
}) => {
  const maxFontSize = getSafeMaxFontSize();
  const initialFontSize = Math.min(maxFontSize * 0.65, 80);

  useEffect(() => {
    if (isAutoFitDebugActive()) {
      addDebugLog('ImageLayout', 'Layout renderizado', {
        title,
        textScale: screenResolution.textScale,
        isLargeScreen: screenResolution.isLargeScreen,
        maxFontSize,
        initialFontSize,
        minFontSize: 12,
        lineHeight: 1.4,
        contentLength: processedWork?.length || 0,
        hasImage: !!imageUrl,
      });
    }
  }, [title, screenResolution.textScale, screenResolution.isLargeScreen, maxFontSize, initialFontSize, processedWork, imageUrl]);

  return (
    <div
      className="relative w-full h-full flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url("${imageUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <DialogHeader title={title} blockSpecs={blockSpecs} titleStyle="large" variant="dark" />
      <div className="relative z-10 w-full h-full flex flex-col px-12 py-8">
        <div className="flex-1 flex items-start justify-center pt-16 pb-2 min-h-0 overflow-hidden">

          <AutoFitText
            maxHeight="100%"
            padding={0}
            minFontSize={12}
            maxFontSize={maxFontSize}
            initialFontSize={initialFontSize}
            lineHeight={1.4}
          >
            <DialogContentStyles />
            <div className="prose max-w-none text-white text-center fullscreen-dialog-content">
              {parse(processedWork || '', parserOptions)}
            </div>
          </AutoFitText>
        </div>
      </div>
    </div>
  );
};

/* ========================= VIDEO ========================= */
export const VideoLayout: React.FC<VideoLayoutProps> = ({
  title,
  processedWork,
  parserOptions,
  blockSpecs,
  screenResolution,
  mediaHtml,
  shouldLoadMedia,
}) => {
  const maxFontSize = getSafeMaxFontSize();
  const initialFontSize = Math.min(maxFontSize * 0.6, 70);

  useEffect(() => {
    if (isAutoFitDebugActive()) {
      addDebugLog('VideoLayout', 'Layout renderizado', {
        title,
        textScale: screenResolution.textScale,
        isLargeScreen: screenResolution.isLargeScreen,
        maxFontSize,
        initialFontSize,
        minFontSize: 12,
        lineHeight: 1.4,
        contentLength: processedWork?.length || 0,
        hasMedia: !!mediaHtml,
      });
    }
  }, [title, screenResolution.textScale, screenResolution.isLargeScreen, maxFontSize, initialFontSize, processedWork, mediaHtml]);

  return (
    <div className="w-full h-full relative flex flex-col p-6">
      <SimpleHeader title={title} />
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-stretch pt-16 min-h-0">

        <div className="flex flex-col gap-4">
          <div className="w-auto h-auto max-w-full max-h-[80vh] min-w-[45vw] min-h-[45vh] flex items-center justify-center">
            {shouldLoadMedia ? parse(mediaHtml || '', parserOptions) : <LoadingSpinner />}
          </div>
          <SpecsContainer specs={blockSpecs?.flat() || []} />
        </div>
        <div className="flex items-start justify-center overflow-hidden">
          <AutoFitText
            maxHeight="100%"
            padding={0}
            minFontSize={12}
            maxFontSize={maxFontSize}
            initialFontSize={initialFontSize}
            lineHeight={1.4}
          >
            <DialogContentStyles />
            <div className="prose max-w-none text-white fullscreen-dialog-content">
              {parse(processedWork || '', parserOptions)}
            </div>
          </AutoFitText>
        </div>
      </div>
    </div>
  );
};

/* ========================= NORMAL ========================= */
export const NormalLayout = ({
  processedWork,
  parserOptions,
  mediaHtml,
  gridClass,
  shouldLoadMedia,
  isImageMedia,
  extractImageUrl,
}: any) => {
  if (!mediaHtml) {
    return (
      <div className="fullscreen-dialog-content flex items-center justify-center min-h-[45vh] min-w-[45vw] px-8 py-6">
        <DialogContentStyles />
        <div className="prose max-w-none text-white text-center" style={{ fontSize: '20px', lineHeight: '1.6' }}>
          {parse(processedWork || '', parserOptions)}
        </div>
      </div>
    );
  }

  if (isImageMedia(mediaHtml)) {
    return (
      <div
        className="relative flex items-center justify-center min-h-[60vh] min-w-[60vw] overflow-hidden rounded-lg"
        style={{
          backgroundImage: `url("${extractImageUrl(mediaHtml)}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
        <div className="fullscreen-dialog-content relative z-10 flex items-center justify-center px-12 py-8 max-w-[80%]">
          <DialogContentStyles />
          <div className="prose prose-lg max-w-none text-white text-center" style={{ fontSize: '22px', lineHeight: '1.6' }}>
            {parse(processedWork || '', parserOptions)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={gridClass}>
      <div className="w-auto h-auto max-w-full max-h-[80vh] min-w-[45vw] min-h-[45vh]">
        {shouldLoadMedia ? parse(mediaHtml || '', parserOptions) : <LoadingSpinner />}
      </div>
      <div className="fullscreen-dialog-content prose max-w-auto text-white mt-4 mb-4 mr-4">
        <DialogContentStyles />
        <div style={{ fontSize: '20px', lineHeight: '1.6' }}>
          {parse(processedWork || '', parserOptions)}
        </div>
      </div>
    </div>
  );
};